import React, { PropsWithChildren, useState } from 'react'
import { t } from '@lingui/macro'

import { Badge, IconButton, Popover, SxProps, Theme, Tooltip } from '@mui/material'
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded'

import NotificationMenuItem from './NotificationMenuItem'

export type NotificationMenuProps = PropsWithChildren<{
  onClose?: () => void
  maxHeight?: string
  unseenCount: number
  icon?: React.ReactNode
  tooltip?: string
  sx?: SxProps<Theme>
}>

function NotificationMenu({ children, onClose, unseenCount, icon, tooltip, maxHeight, sx }: NotificationMenuProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    // for all events that are unread by this user mark them as unread
    onClose && onClose()
    setAnchorEl(null)
  }

  return (
    <div>
      <Tooltip title={tooltip ?? t`Notifications`}>
        <IconButton
          aria-label="notifications"
          aria-controls="simple-menu"
          aria-haspopup="true"
          onClick={handleClick}
          sx={[
            {
              width: 40,
              height: 40,
              background: 'transparent',
              border: (theme) => `1px solid ${theme.palette.primary.light}`,
              color: (theme) => theme.palette.background.default,
              position: 'relative',
              '&:hover': {
                backgroundColor: (theme) => theme.palette.primary.light,
              },
              '& .HW_absolute': {
                position: 'relative',
              },
              '& .HW_badge_cont .HW_visible': {
                transform: 'none',
                backgroundColor: '#D80000',
              },
              '& .HW_badge': {
                visibility: 'hidden',
              },
            },
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
        >
          <Badge
            badgeContent={unseenCount}
            color="error"
            overlap="circular"
            sx={{
              position: 'unset',
              '& .MuiBadge-badge': {
                transform: 'scale(0.8) translate(50%, -50%)',
              },
            }}
          >
            {icon ?? <NotificationsNoneRoundedIcon />}
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        sx={{
          maxWidth: 400,
          maxHeight,
          '& .MuiPopover-paper': {
            overflow: 'hidden',
            maxHeight: '100%',
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {children}
      </Popover>
    </div>
  )
}

NotificationMenu.Item = NotificationMenuItem

export default NotificationMenu
